import Map from "ol/Map.js";
import View from "ol/View.js";
import { fromLonLat } from "ol/proj";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getItemWebStorage } from "../../../features/webStorageSaver";
import { useAppSelector } from "../../../stateManagement/hooks/redux";
import { setFigureList, setGeoMarkerList, setHighlightObjectGeoJSON, setHighlightObjectStyleConfig, setOverlayList } from "../../../stateManagement/reducers/MapSlice";
import { MapInfoWidget } from "../mapAddons/mapInfoWidget/MapInfoWidget";
import { countExtentFromGeoJSONFeatures, countExtentFromWKTFeatures, formatExtent, getImageScale, localStateReloader } from "../mapFeatures/helpers";
import { getAeroLayer, getEsriWorldImageryLayer, getGeoJSONFeaturesVectorLayer, getRasterLayer, getWKTFeatureVectorLayer, applyMapSettingsAdditionalLayers, getGeoJSONFeaturesVectorLayers, getAllObjectsLayer, getMapPageAdditionalLayers } from "../mapFeatures/layers";
import { activateCopyPointCoordinates } from "../mapFeatures/mapFunctions/copyPointCoordinates/copyPointCoordinates";
import { activateMeasureFunctions } from "../mapFeatures/mapFunctions/measureFunctions/measureInteractions";
import { activateRosreestrCadasterLayer } from "../mapFeatures/mapFunctions/rosreestrFunctions/rosreestrFunctions";
import { activateShowUserLocationFunction } from "../mapFeatures/mapFunctions/showUserLocation/showUserLocation";
import { activateMonitorScaleInfo } from "../mapFeatures/mapFunctions/mapInfoFunctions/scaleInfoFunctions";
import { applyMapLoaderForLayer, applyMapLoaderForMap } from "../mapFeatures/mapFunctions/mapLoader/MapLoader";
import { getLineStyle, getStyleForRosreestrFeature } from "../mapFeatures/styles/mapStyles";
import { useLocation, useNavigate } from "react-router-dom";
import { store } from "../../../stateManagement/store";
import { changeCameraMarkerPosition } from "../mapFeatures/mapFunctions/getFrameOnMapPoint/getFrameOnMapPoint";
import { parseMapUrl } from "../mapFeatures/mapFunctions/parseMapUrl/parseMapUrl";
import { setNavigateToPanoButtonIsClicked, setRosreestrIsAvailable } from "../../../stateManagement/reducers/ContentHeaderSlice";
import MapRosreestrWidget from "../mapAddons/mapRosreestrWidget/MapRosreestrWidget";
import { setNumberOfSelectedRosreestrObjects, setRosreestrObjectsInfoModalIsShown, setSelectedRosreestrObjectsByGroupId } from "../../../stateManagement/reducers/RosreestrSlice";
import MapSearchbar from "../../searchbars/mapSearchbar/MapSearchbar";
import { activateAddGeoMarkersFunction } from "../mapFeatures/mapFunctions/addGeoMarkers/addGeoMarkers";
import { activateAddOverlaysFunction } from "../mapFeatures/mapFunctions/addOverlays/addOverlays";
import { activateAddFiguresFunction } from "../mapFeatures/mapFunctions/addFigures/addFigures";
import { MapCopyCoordinatesWidget } from "../mapAddons/mapCopyCoordinatesWidget";
import { showToastMessage } from "../../../stateManagement/reducers";
import ToastMessageContainer from "../../toastMessageContainer/ToastMessageContainer";
import { activateGoToYandexMirrors } from "../mapFeatures/mapFunctions/goToYandexMirrors/goToYandexMirrors";
import { defaults as defaultControls } from "ol/control/defaults";
import { useMapURLParams } from "../../../hooks/map/useMapParams";
import { activateConfiguredLayerClickInteractions } from "../mapFeatures/mapFunctions/configuredLayerClickInteraction/configuredLayerClickInteraction";
import { clearSearchMapArtifacts } from "../../searchbars/mapSearchbar/hooks/searchMapState";
import { activateTrafficPlanPositionSelection } from "../mapFeatures/mapFunctions/trafficPlanPositionSelection/trafficPlanPositionSelection";
import SmallLoader from "../../loaders/SmallLoader";

export default function MapWidget({ mapStateReturnFunc, target, propsWidth, propsHeight, permanentLayers, additionalLayers, propsLongitude, propsLatitude,
    propsZoom, propsMaxZoom, propsMinZoom, centerOnObjectCoordinates, propsFitExtent, propsFitExtentPadding, mapEventHandlers,
    mapInteractions, mapSizeClass, mapType, mapInfoWidgetIsShown = true, mapInfoWidgetPositionIsTop = false, showMapLoader = true, highlightObjectColor,
    trafficPlanPositionSelectionLayer }: any) {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { mapName, mapOption } = useMapURLParams()
    const mapRef = useRef<Map>();
    const mapLoaderRef = useRef(null)
    const currentConfig = getItemWebStorage('currentConfig') ?? {}
    const {
        mapDefaults = {},
        mapPageAdditionalLayers = [],
        wmsServer,
        wmsVersion,
        workspace
    } = currentConfig
    const {
        extent: localExtent,
        minZoom: configMinZoom,
        maxZoom: configMaxZoom,
        center: mapDefaultsCenter = [0, 0],
        zoom: configZoom
    } = mapDefaults
    const { urlZoom, urlLongitude, urlLatitude } = parseMapUrl()
    const minZoom = propsMinZoom || configMinZoom || 5
    const maxZoom = propsMaxZoom || configMaxZoom || 20
    const localMapPosition = getItemWebStorage('mapPosition')
    const [propsCoordX, propsCoordY] = fromLonLat([propsLongitude, propsLatitude])
    const [mapStateCoordX, mapStateCoordY] = fromLonLat([localMapPosition?.longitude, localMapPosition?.latitude]);
    const [urlCoordX, urlCoordY] = fromLonLat([urlLongitude, urlLatitude]);
    const [mapDefaultsCoordX, mapDefaultsCoordY] = fromLonLat(mapDefaultsCenter);
    const coordX = propsCoordX || urlCoordX || mapStateCoordX || mapDefaultsCoordX || 0
    const coordY = propsCoordY || urlCoordY || mapStateCoordY || mapDefaultsCoordY || 0
    const zoom = propsZoom || urlZoom || localMapPosition?.zoom || configZoom || 5
    let center = [coordX, coordY]
    const extent = formatExtent(localExtent)
    const { mapPosition, highlightObjectWKTGeometry, highlightObjectGeoJSON, highlightObjectStyleConfig, geoMarkerList, overlayList, figureList } = useAppSelector(state => state.map)
    const { areaMeasurementIsEnabled, copyCoordsButtonIsClicked, copyCoordsTypeIsChosen,
        highContrastModeIsEnabled, navigateToPanoButtonIsClicked, perimeterMeasurementIsEnabled,
        rosreestrIsAvailable, rosreestrIsEnabled, searchLineIsShown, showUserLocationButtonIsClicked,
        goToYandexMirrorsButtonIsClicked, trafficPlanPositionSelectionIsEnabled } = useAppSelector(state => state.contentHeader)
    const rosreestrObjectsInfoModalIsShown = useAppSelector(state => state.rosreestr.rosreestrObjectsInfoModalIsShown)
    const mapInfoIsEnabled = useAppSelector((state) => state.mapInfo.mapInfoIsShown)
    const mapTooltipIsEnabled = useAppSelector(state => state.contentHeader.mapTooltipIsEnabled)
    const { auxiliaryLayersDisplayState, additionalLayersDisplayState } = getItemWebStorage("mapSettings") ?? {}
    const { esriWorldImagery: mapEsriWorldImageryLayerIsShown, indorAvia: mapIndorAviaLayerIsShown, allObjects: areAllObjectsShown } = auxiliaryLayersDisplayState ?? {}
    // Подписку на изменения прозрачности переносим на store.subscribe ниже, чтобы не триггерить ре-рендеры
    const auxiliaryLayersDisplayStateRedux = useAppSelector(state => state.mapSettings.auxiliaryLayersDisplayState)
    const additionalLayersDisplayStateRedux = useAppSelector(state => state.mapSettings.additionalLayersDisplayState)
    const [alertText, setAlertsShowerText] = useState<any>('')
    const [trafficPlanIsOpening, setTrafficPlanIsOpening] = useState(false)
    const [currentFitExtent, setCurrentFitExtent] = useState<any>()
    const [currentFitExtentPadding, setCurrentFitExtentPadding] = useState<any>()
    const mapTargetId = target ?? 'map'
    const mapPageAdditionalLayersConfigHash = JSON.stringify(mapPageAdditionalLayers)
    const configuredPageAdditionalLayers = useMemo(() => {
        return getMapPageAdditionalLayers(currentConfig, {
            mapName,
            mapOption,
            pathname: location.pathname
        });
    }, [location.pathname, mapName, mapOption, mapPageAdditionalLayersConfigHash, wmsServer, wmsVersion, workspace])
    const resolvedAdditionalLayers = useMemo(() => {
        return [...configuredPageAdditionalLayers, ...(additionalLayers ?? [])]
    }, [additionalLayers, configuredPageAdditionalLayers])

    useEffect(() => {
        if (propsFitExtent) setCurrentFitExtent(propsFitExtent)
        if (propsFitExtentPadding) setCurrentFitExtentPadding(propsFitExtentPadding)
    }, [propsFitExtent, propsFitExtentPadding])

    useEffect(() => {
        if (mapPosition) {
            const { longitude, latitude, zoom } = mapPosition
            mapRef.current?.getView().setCenter(fromLonLat([longitude, latitude]))
            mapRef.current?.getView().setZoom(zoom)
            if (mapType !== 'panorama') {
                changeCameraMarkerPosition(longitude, latitude)
            }
        }
    }, [mapPosition])

    // Создание карты - базовые функции
    useEffect(() => {
        let basicLayers = [getRasterLayer(highContrastModeIsEnabled)]
        // Добавление слоев спутниковой съемки Esri World Imagery
        if (mapEsriWorldImageryLayerIsShown) {
            let esriWorldImageryLayer: any = getEsriWorldImageryLayer()
            basicLayers.push(esriWorldImageryLayer);
        }
        if (areAllObjectsShown) {
            let roadsLayer: any = getAllObjectsLayer()
            basicLayers.push(roadsLayer);
        }
        // Добавление дополнительных уникальных слоев
        if (additionalLayersDisplayState) {
            basicLayers = applyMapSettingsAdditionalLayers(additionalLayersDisplayState, currentConfig, basicLayers)
        }
        // Добавление слоев съемки indorAvia
        if (mapIndorAviaLayerIsShown) {
            let aeroLayer: any = getAeroLayer()
            basicLayers.push(aeroLayer);
        }
        const map = new Map({
            moveTolerance: 3, // Клик при перемещении до 3 пикселей
            target: target ?? 'map',
            view: new View({
                projection: 'EPSG:3857',
                showFullExtent: true,
                smoothResolutionConstraint: true,
                smoothExtentConstraint: true,
                constrainResolution: true,
                constrainOnlyCenter: true,
                center: center,
                extent: extent,
                minZoom: minZoom,
                maxZoom: maxZoom,
                zoom: zoom
            }),
            controls: defaultControls({
                zoomOptions: {
                    zoomInTipLabel: 'Приблизить',
                    zoomOutTipLabel: 'Отдалить',
                },
            }),
            pixelRatio: getImageScale().imageScale,
            layers: basicLayers
        });

        // Обозначаем объект карты
        mapRef.current = map;
        if (mapStateReturnFunc) mapStateReturnFunc(map)
        mapRef.current.on("moveend", () => localStateReloader(mapRef.current));

        if (showMapLoader) applyMapLoaderForMap(mapLoaderRef.current, mapRef.current)
        basicLayers.forEach((layer) => {
            if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, layer)
        });

        return () => {
            map.setTarget(undefined);
            dispatch(setHighlightObjectStyleConfig(null))
            dispatch(setHighlightObjectGeoJSON(null))
            dispatch(setRosreestrObjectsInfoModalIsShown(false))
            dispatch(setGeoMarkerList([]))
            dispatch(setOverlayList([]))
            dispatch(setFigureList([]))
        };
    }, []);

    // Живое управление видимостью вспомогательных слоёв
    useEffect(() => {
        if (!mapRef.current) return;
        const layers: any[] = mapRef.current.getAllLayers?.() ?? [];
        const ensureLayer = (className: string, creator: () => any, visible: boolean) => {
            const existing = layers.find((l: any) => l?.getClassName?.() === className);
            if (visible && !existing) {
                const layer = creator();
                mapRef.current?.addLayer(layer);
                if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, layer)
            }
            if (!visible && existing) {
                mapRef.current?.removeLayer(existing);
            }
        };
        ensureLayer('aux-esriWorldImagery', getEsriWorldImageryLayer, !!auxiliaryLayersDisplayStateRedux?.esriWorldImagery);
        ensureLayer('aux-indorAvia', getAeroLayer, !!auxiliaryLayersDisplayStateRedux?.indorAvia);
        ensureLayer('aux-allObjects', getAllObjectsLayer, !!auxiliaryLayersDisplayStateRedux?.allObjects);
    }, [auxiliaryLayersDisplayStateRedux]);

    // Живое управление видимостью доп. слоёв
    useEffect(() => {
        if (!mapRef.current) return;
        const activeKeys = Object.keys(additionalLayersDisplayStateRedux ?? {}).filter(k => additionalLayersDisplayStateRedux[k]);
        const layers: any[] = mapRef.current.getAllLayers?.() ?? [];
        const extractAdditionalKey = (cn?: string) => {
            if (!cn) return undefined;
            const idx = cn.indexOf('additional-');
            if (idx < 0) return undefined;
            const tail = cn.slice(idx + 'additional-'.length);
            const end = tail.search(/\s/);
            return end === -1 ? tail : tail.slice(0, end);
        };
        const existingKeys = layers
            .map((l: any) => extractAdditionalKey(l?.getClassName?.()))
            .filter((v: any) => !!v);

        // Добавить недостающие
        const toAdd = activeKeys.filter(k => !existingKeys.includes(k));
        if (toAdd.length > 0) {
            const subset = toAdd.reduce((acc: any, k: string) => ({ ...acc, [k]: true }), {});
            let arr: any = [];
            applyMapSettingsAdditionalLayers(subset, currentConfig, arr);
            arr.forEach((layer: any) => {
                mapRef.current?.addLayer(layer);
                if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, layer)
            });
        }
        // Удалить выключенные
        const toRemove = (existingKeys as string[]).filter(k => !activeKeys.includes(k));
        toRemove.forEach(k => {
            const layer = layers.find((l: any) => (l?.getClassName?.() || '').includes(`additional-${k}`));
            if (layer) mapRef.current?.removeLayer(layer);
        });
    }, [additionalLayersDisplayStateRedux]);

    // Подписка на изменение прозрачности в store — без перерендера компонента
    useEffect(() => {
        let frameReq = 0 as any;
        const unsubscribe = store.subscribe(() => {
            if (!mapRef.current) return;
            const state = store.getState();
            const { auxiliaryLayersOpacityState, additionalLayersOpacityState, rosreestrLayersOpacityState } = state.mapSettings ?? {} as any;
            if (frameReq) cancelAnimationFrame(frameReq);
            frameReq = requestAnimationFrame(() => {
                const layers: any[] = mapRef.current?.getAllLayers?.() ?? [];
                const setOpacityByClass = (className: string, value?: number) => {
                    const layer = layers.find((l: any) => (l?.getClassName?.() || '').includes(className));
                    layer?.setOpacity?.(typeof value === 'number' ? value : 1);
                };
                // auxiliary
                setOpacityByClass('aux-esriWorldImagery', auxiliaryLayersOpacityState?.esriWorldImagery);
                setOpacityByClass('aux-indorAvia', auxiliaryLayersOpacityState?.indorAvia);
                setOpacityByClass('aux-allObjects', auxiliaryLayersOpacityState?.allObjects);
                // additional
                Object.entries(additionalLayersOpacityState ?? {}).forEach(([key, val]: any) => {
                    setOpacityByClass(`additional-${key}`, val as number);
                });
                // rosreestr
                Object.entries(rosreestrLayersOpacityState ?? {}).forEach(([key, val]: any) => {
                    setOpacityByClass(`rosreestr-${key}`, val as number);
                    setOpacityByClass(`rosreestr-${key}-labels`, val as number);
                });
            });
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (permanentLayers?.length > 0) {
            permanentLayers.map((layer: any) => {
                if (layer) {
                    mapRef.current?.addLayer(layer)
                    if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, layer)
                }
            })
        }
        return () => {
            permanentLayers?.map((layer: any) => {
                mapRef.current?.removeLayer(layer)
            })
        }
    }, [permanentLayers])

    // Добавление дополнительных слоев
    useEffect(() => {
        if (resolvedAdditionalLayers?.length > 0) {
            resolvedAdditionalLayers.map((layer: any) => {
                if (layer) {
                    mapRef.current?.addLayer(layer)
                    if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, layer)
                }
            })
        }
        return () => {
            resolvedAdditionalLayers?.map((layer: any) => {
                mapRef.current?.removeLayer(layer)
            })
        }
    }, [resolvedAdditionalLayers])

    useEffect(() => {
        const styleFromConfig = (cfg: any) => {
            if (!cfg) return getLineStyle(highlightObjectColor, 4)
            if (cfg.kind === 'line') {
                return getLineStyle(cfg.color ?? highlightObjectColor, cfg.width ?? 4, cfg.lineDash)
            }
            return getLineStyle(highlightObjectColor, 4)
        }

        const lineStyle = styleFromConfig(highlightObjectStyleConfig)
        const highlightObjectLayer = highlightObjectWKTGeometry && getWKTFeatureVectorLayer(highlightObjectWKTGeometry, lineStyle)
        if (highlightObjectLayer) {
            // mapRef.current?.addLayer(highlightObjectLayer)
            if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, highlightObjectLayer)
            // По умолчанию подсветка "фитит" карту. Для некоторых сценариев (например, поиск по координатам)
            // нужно подсветить дорогу, но НЕ менять зум/экстент — используем флаг fit:false в styleConfig.
            if (highlightObjectStyleConfig?.fit !== false) {
                const extent = countExtentFromWKTFeatures(highlightObjectWKTGeometry)
                setCurrentFitExtent(extent)
                setCurrentFitExtentPadding([50, 50, 50, 50])
            }
        }
        return () => {
            mapRef.current?.removeLayer(highlightObjectLayer)
        }
    }, [highlightObjectWKTGeometry, highlightObjectStyleConfig])

    useEffect(() => {
        if (Array.isArray(highlightObjectGeoJSON)) {
            // добавить обработку стиля не только для объектов Росреестра
            var highlightObjectLayers = highlightObjectGeoJSON && getGeoJSONFeaturesVectorLayers(highlightObjectGeoJSON, 'EPSG:3857', getStyleForRosreestrFeature)
            if (highlightObjectLayers.length > 0) {
                for (const highlightObjectLayer of highlightObjectLayers) {
                    mapRef.current?.addLayer(highlightObjectLayer)
                    if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, highlightObjectLayer)
                }
            }
        } else {
            const styleFromConfig = (cfg: any) => {
                if (!cfg) return getLineStyle(highlightObjectColor, 4)
                if (cfg.kind === 'line') return getLineStyle(cfg.color ?? highlightObjectColor, cfg.width ?? 4, cfg.lineDash)
                return getLineStyle(highlightObjectColor, 4)
            }
            const resolvedStyle = styleFromConfig(highlightObjectStyleConfig)
            var highlightObjectLayer = highlightObjectGeoJSON && getGeoJSONFeaturesVectorLayer(highlightObjectGeoJSON, 'EPSG:3857', resolvedStyle)
            if (highlightObjectLayer) {
                mapRef.current?.addLayer(highlightObjectLayer)
                if (showMapLoader) applyMapLoaderForLayer(mapLoaderRef.current, highlightObjectLayer)
                if (highlightObjectStyleConfig?.fit !== false) {
                    const extent = countExtentFromGeoJSONFeatures(highlightObjectGeoJSON, true)
                    setCurrentFitExtent(extent)
                    setCurrentFitExtentPadding([50, 50, 50, 50])
                }
            }
        }

        return () => {
            if (highlightObjectLayers?.length > 0) {
                for (const highlightObjectLayer of highlightObjectLayers) {
                    mapRef.current?.removeLayer(highlightObjectLayer)
                }
            } else {
                mapRef.current?.removeLayer(highlightObjectLayer)
            }
        }
    }, [highlightObjectGeoJSON, highlightObjectStyleConfig])

    // Режим высокой контрастности
    useEffect(() => {
        let newRasterLayer = getRasterLayer(highContrastModeIsEnabled)
        let layers: any = mapRef.current?.getAllLayers()
        layers.shift()
        mapRef.current?.setLayers([newRasterLayer, ...layers])
    }, [highContrastModeIsEnabled])

    // Добавление измерений на карту
    useEffect(() => {
        const measureFunction = activateMeasureFunctions(mapRef.current, perimeterMeasurementIsEnabled, areaMeasurementIsEnabled)
        return () => {
            measureFunction.off()
        }
    }, [perimeterMeasurementIsEnabled, areaMeasurementIsEnabled])

    // Обработка отсутствия доступа к Росреестру
    useEffect(() => {
        if (!rosreestrIsAvailable) {
            dispatch(showToastMessage({ messageText: 'Использование данной функции возможно только при открытии Геопортала из браузера, внесенного в реестр ПО РФ (сайт перешел на российские сертификаты безопасности)', messageType: "error", windowType: "map" }))
            dispatch(setRosreestrIsAvailable(true))
        }
    }, [rosreestrIsAvailable])

    // Подключение слоя Росреестра
    useEffect(() => {
        const rosreestrCadasterLayerFunction = activateRosreestrCadasterLayer(mapType, rosreestrIsEnabled, setAlertsShowerText, mapRef.current)
        if (!rosreestrIsEnabled) {
            dispatch(setHighlightObjectGeoJSON(null))
            dispatch(setNumberOfSelectedRosreestrObjects(0))
            dispatch(setSelectedRosreestrObjectsByGroupId(null))
        }
        return () => {
            rosreestrCadasterLayerFunction.off()
        }
    }, [rosreestrIsEnabled])

    // Функция для копирования координат точки на карте
    useEffect(() => {
        const copyCoordsFunction = activateCopyPointCoordinates(copyCoordsTypeIsChosen, copyCoordsButtonIsClicked, mapRef.current)
        return () => {
            copyCoordsFunction.off()
        }
    }, [copyCoordsTypeIsChosen, copyCoordsButtonIsClicked])

    // Функция для копирования координат точки на карте
    useEffect(() => {
        const goToYandexMirrorsFunction = activateGoToYandexMirrors(goToYandexMirrorsButtonIsClicked, mapRef.current)
        return () => {
            goToYandexMirrorsFunction.off()
        }
    }, [goToYandexMirrorsButtonIsClicked])

    useEffect(() => {
        const trafficPlanPositionSelection = activateTrafficPlanPositionSelection(
            trafficPlanPositionSelectionIsEnabled,
            trafficPlanPositionSelectionLayer,
            mapRef.current,
            setTrafficPlanIsOpening,
        )
        return () => {
            trafficPlanPositionSelection.off()
        }
    }, [trafficPlanPositionSelectionIsEnabled, trafficPlanPositionSelectionLayer])

    useEffect(() => {
        if (navigateToPanoButtonIsClicked) {
            dispatch(setNavigateToPanoButtonIsClicked(false))
            navigate('/app/panorama')
        }
    }, [navigateToPanoButtonIsClicked])

    // Функция для отображения текущего местоположения пользователя
    useEffect(() => {
        const showUserLocationFunction = activateShowUserLocationFunction(showUserLocationButtonIsClicked, localExtent, mapRef.current)
        return () => {
            showUserLocationFunction.off()
        }
    }, [showUserLocationButtonIsClicked])

    // Функция для расставления маркеров на карте
    useEffect(() => {
        const addGeoMarkersFunction = activateAddGeoMarkersFunction(geoMarkerList, mapRef.current)
        return () => {
            addGeoMarkersFunction.off()
        }
    }, [geoMarkerList])

    // Функция для добавления оверлеев на карту (аналогично маркерам)
    useEffect(() => {
        const addOverlaysFunction = activateAddOverlaysFunction(overlayList, mapRef.current)
        return () => {
            addOverlaysFunction.off()
        }
    }, [overlayList])

    // Фигуры (пунктиры/окружности/и т.п.
    useEffect(() => {
        const f = activateAddFiguresFunction(figureList, mapRef.current)
        return () => f.off()
    }, [figureList])

    // Отключение отображения результатов поиска при скрытии поисковой строки
    useEffect(() => {
        if (!searchLineIsShown) {
            clearSearchMapArtifacts()
        }
    }, [searchLineIsShown])

    useEffect(() => {
        const activateMonitorScaleInfoFunction = activateMonitorScaleInfo(mapInfoIsEnabled, mapRef.current)
        return () => {
            activateMonitorScaleInfoFunction?.off()
        }
    }, [mapInfoIsEnabled])

    useEffect(() => {
        const anyOfMapToolsWithClickInteraction = (copyCoordsButtonIsClicked || perimeterMeasurementIsEnabled || areaMeasurementIsEnabled || goToYandexMirrorsButtonIsClicked || trafficPlanPositionSelectionIsEnabled)
        if (anyOfMapToolsWithClickInteraction) return;

        const configuredLayerClickInteractions = activateConfiguredLayerClickInteractions(resolvedAdditionalLayers, mapRef.current)
        return () => {
            configuredLayerClickInteractions.off()
        }
    }, [resolvedAdditionalLayers, copyCoordsButtonIsClicked, perimeterMeasurementIsEnabled, areaMeasurementIsEnabled, goToYandexMirrorsButtonIsClicked, trafficPlanPositionSelectionIsEnabled])

    useEffect(() => {
        if (currentFitExtent) {
            mapRef.current?.getView().fit(currentFitExtent, {
                size: mapRef.current.getSize(),
                padding: currentFitExtentPadding,
            });
        }
    }, [currentFitExtent, currentFitExtentPadding])

    useEffect(() => {
        if (centerOnObjectCoordinates) {
            let mapSize: any = mapRef?.current?.getSize()
            mapRef?.current?.getView()?.centerOn(centerOnObjectCoordinates, mapSize, [mapSize[0] / 2, mapSize[1] / 2])
        }
    }, [centerOnObjectCoordinates])

    useEffect(() => {
        const mapElement = document.getElementById(mapTargetId);
        if (!mapElement || !mapRef.current) return;

        const updateMapSize = () => {
            window.requestAnimationFrame(() => {
                mapRef.current?.updateSize();
            });
        };

        updateMapSize();
        window.addEventListener('resize', updateMapSize);

        let resizeObserver: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                updateMapSize();
            });
            resizeObserver.observe(mapElement);
        }

        return () => {
            window.removeEventListener('resize', updateMapSize);
            resizeObserver?.disconnect();
        };
    }, [mapTargetId, mapInfoIsEnabled]);

    // Обработчик событий карты
    useEffect(() => {
        // Режим поиска (searchLineIsShown) НЕ должен блокировать клики по карте и поиск объектов в точке.
        // Блокируем только инструменты, которые реально перехватывают клик.
        const anyOfMapToolsWithClickInteraction = (copyCoordsButtonIsClicked || perimeterMeasurementIsEnabled || areaMeasurementIsEnabled || goToYandexMirrorsButtonIsClicked || trafficPlanPositionSelectionIsEnabled)
        let mapEventHandlersFunctions: any = []
        if (!anyOfMapToolsWithClickInteraction) {
            mapEventHandlers && mapEventHandlers?.map((mapEventHandler: any) => {
                if (mapEventHandler?.name === "mapTooltip" && mapTooltipIsEnabled) {
                    mapEventHandlersFunctions.push(mapEventHandler?.func(mapRef?.current))
                }
                if (mapEventHandler?.name === "objectsFind" || mapEventHandler?.name === "transportInteraction") {
                    mapEventHandlersFunctions.push(mapEventHandler?.func(mapRef?.current))
                }
                if (mapEventHandler?.name === "openFullSizeMap" || mapEventHandler?.name === "synchronizePrintMap") {
                    mapEventHandlersFunctions.push(mapEventHandler?.func(mapRef?.current))
                }
                if (mapEventHandler?.name === "frameFind" || mapEventHandler?.name === "panoramaFiltersDefine" || mapEventHandler?.name === "rotatePanoramaCamera") {
                    mapEventHandlersFunctions.push(mapEventHandler?.func(mapRef?.current))
                }
            })
        }
        return () => {
            mapEventHandlersFunctions && mapEventHandlersFunctions.map((func: any) => {
                func.off()
            })
        }
    }, [mapEventHandlers, resolvedAdditionalLayers, copyCoordsButtonIsClicked, perimeterMeasurementIsEnabled,
        areaMeasurementIsEnabled, searchLineIsShown, mapTooltipIsEnabled, goToYandexMirrorsButtonIsClicked, trafficPlanPositionSelectionIsEnabled])

    useEffect(() => {
        let mapInteractionsFunctions: any = []
        mapInteractions && mapInteractions.map((mapInteraction: any) => {
            if (mapInteraction?.name === "modifyPanoramaCamera") {
                mapInteractionsFunctions.push(mapInteraction?.func(mapRef?.current))
            }
        })
        return () => {
            mapInteractionsFunctions && mapInteractionsFunctions.map((func: any) => {
                func.off()
            })
        }
    }, [mapInteractions])

    return (
        <>
            {searchLineIsShown && mapType !== "panorama" && mapType !== "card" && <MapSearchbar />}
            {mapInfoWidgetIsShown && mapInfoWidgetPositionIsTop &&
                <MapInfoWidget />
            }
            {searchLineIsShown && mapType === "panorama" && <MapSearchbar />}
            <div className="map-widget-container">
                <div id={mapTargetId} style={{ width: propsWidth, height: propsHeight }} className={(mapSizeClass ?? "full-size-map-widget") + (mapInfoWidgetIsShown ? ' with-scale-widget' : '')}>
                    {/* Загрузчик карты */}
                    {showMapLoader &&
                        <div className="map-loader-wrapper">
                            <div id="progress" ref={mapLoaderRef} />
                        </div>
                    }
                </div>
                <ToastMessageContainer windowType="map" />
                {trafficPlanIsOpening &&
                    <div className="traffic-plan-opening-loader">
                        <SmallLoader text="Открываю IndorTrafficPlan..." />
                    </div>
                }
            </div>
            {/* Счетчик не пришедших тайлов Росреестра */}
            {alertText && rosreestrIsEnabled && <div className="rosreestr-notification">{alertText}</div>}
            {/* Блок выбора формата копируемых координат */}
            {copyCoordsButtonIsClicked && <MapCopyCoordinatesWidget isCopyButtonTypeChosen={copyCoordsTypeIsChosen} />}
            {(rosreestrObjectsInfoModalIsShown && rosreestrIsEnabled) && <MapRosreestrWidget />}
            {/* Виджет с масштабом, координатами */}
            {mapInfoWidgetIsShown && !mapInfoWidgetPositionIsTop &&
                <MapInfoWidget />
            }
        </>
    )
}
